import { withJsonFormsControlProps } from "@jsonforms/react";
import { Box, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ApiLojaGet } from "data/ApiLoja";
import { CustomRendererProps } from "../types";

type Session = {
  id?: string;
  title?: string;
  methods?: string[];
  qrCheckInUrl?: string | null;
};

type AttendanceData = { sessions: Session[] };

function EventAttendanceSessionQr({ data, path }: CustomRendererProps<Session>) {
  const eventId = Number(useParams()?.id);
  const qrEnabled = Boolean(data?.methods?.includes("QR_CODE"));
  const [qrCheckInUrl, setQrCheckInUrl] = useState("");
  const [loading, setLoading] = useState(false);

  const findSession = useCallback((sessions: Session[]) => {
    if (data?.id) {
      const byId = sessions.find((session) => session.id === data.id);
      if (byId) return byId;
    }

    const indexMatch = path?.match(/sessions(?:\.|\[|\/)(\d+)/);
    const sessionIndex = indexMatch ? Number(indexMatch[1]) : -1;
    if (sessionIndex >= 0 && sessions[sessionIndex]) return sessions[sessionIndex];

    return sessions.find((session) => session.title === data?.title);
  }, [data?.id, data?.title, path]);

  const loadQrCode = useCallback(async (retry = false) => {
    if (!eventId || !qrEnabled) {
      setQrCheckInUrl("");
      return;
    }

    setLoading(true);
    const delays = retry ? [0, 400, 1200] : [0];

    try {
      for (const delay of delays) {
        if (delay) await new Promise((resolve) => window.setTimeout(resolve, delay));
        const response = await ApiLojaGet<AttendanceData>(`/admin/events/${eventId}/attendance`);
        const url = findSession(response.data.sessions)?.qrCheckInUrl;
        if (url) {
          setQrCheckInUrl(url);
          return;
        }
      }
      setQrCheckInUrl("");
    } catch {
      setQrCheckInUrl("");
    } finally {
      setLoading(false);
    }
  }, [eventId, findSession, qrEnabled]);

  useEffect(() => {
    void loadQrCode();
  }, [loadQrCode]);

  useEffect(() => {
    const reloadAfterSave = (event: Event) => {
      const savedEventId = (event as CustomEvent<{ eventId?: number }>).detail?.eventId;
      if (savedEventId === eventId) void loadQrCode(true);
    };

    window.addEventListener("event-attendance-saved", reloadAfterSave);
    return () => window.removeEventListener("event-attendance-saved", reloadAfterSave);
  }, [eventId, loadQrCode]);

  if (!qrEnabled) return null;

  return (
    <Box sx={{ mt: 1, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
      <Stack gap={1.5} alignItems="flex-start">
        <Box>
          <Typography fontWeight="bold">QR Code da sessão</Typography>
          <Typography variant="body2" color="text.secondary">
            Use este QR Code para o check-in dos participantes.
          </Typography>
        </Box>
        {loading && !qrCheckInUrl ? <CircularProgress size={28} /> : null}
        {qrCheckInUrl ? (
          <>
            <Box sx={{ p: 1, borderRadius: 1, bgcolor: "common.white", lineHeight: 0 }}>
              <QRCodeSVG value={qrCheckInUrl} size={220} level="M" />
            </Box>
            <TextField
              fullWidth
              size="small"
              label="URL assinada do QR Code"
              value={qrCheckInUrl}
              InputProps={{ readOnly: true }}
            />
          </>
        ) : null}
      </Stack>
    </Box>
  );
}

export default withJsonFormsControlProps(EventAttendanceSessionQr);
